import createPapiWebSocket from '@paradigm/api/src/internal/createPapiWebSocket';
import {
  ApiWebSocket,
  AsyncWSResp,
  WSNotification,
  WSReqParams,
} from '@paradigm/api/src/internal/createWebSocket';
import { availableToken } from '@paradigm/api/src/internal/token';
import { GrfqWsBbo } from '#/unified-rfqs/entities/grfq/bbo';
import { RawGrfqWsBbo } from './grfq-types';

import { processWsGrfqBbo } from './adapters/grfq-api';
import { getGrfqBaseUrl } from './grfq-api';

export function createSingleGrfqWebSocket(): SingleGrfqWebSocket {
  return createPapiWebSocket({
    urlProvider,
    processNotification,
  });
}

async function urlProvider() {
  const token = await availableToken();
  return (
    `${PAPI_URL_WS}${getGrfqBaseUrl()}/` +
    '?cancel-on-disconnect=0' +
    `&token=${encodeURIComponent(token)}`
  );
}

export interface SingleGrfqWebSocket
  extends Omit<ApiWebSocket<GrfqNotification>, 'request'> {
  readonly request: SingleGrfqWebSocketRequest;
}

interface SingleGrfqWebSocketRequest {
  (req: SubscribeReq): AsyncWSResp<SubscribeResult>;
  (req: UnsubscribeReq): AsyncWSResp<UnsubscribeResult>;
}

interface SubscribeReq extends WSReqParams {
  method: 'subscribe';
  params: {
    channel: SingleGrfqWSChannel<number>;
    data?: unknown;
  };
}

type SubscribeResult = readonly string[];

interface UnsubscribeReq extends WSReqParams {
  method: 'unsubscribe';
  params: {
    channel: SingleGrfqWSChannel<number>;
  };
}

type UnsubscribeResult = readonly string[];

type TickerChannel<T = unknown> = T extends number
  ? `ticker.${number}` | `ticker.${number}.500ms`
  : 'ticker';

export type SingleGrfqWSChannel<T = unknown> = TickerChannel<T>;

interface RawNotificationDataByChannel {
  readonly ticker: RawGrfqWsBbo;
}

export interface NotificationDataByChannel {
  readonly ticker: GrfqWsBbo;
}

type RawSingleGrfqNotificationByChannel = {
  [Channel in SingleGrfqWSChannel]: {
    readonly channel: Channel;
    readonly data: RawNotificationDataByChannel[Channel];
    readonly venue_error?: {
      readonly message: string;
    };
  };
};

export type NotificationErrorMap = {
  readonly venue_error: string | undefined;
};

export type GrfqSingleNotificationByChannel = {
  [Channel in SingleGrfqWSChannel]: {
    readonly channel: Channel;
    readonly data: NotificationDataByChannel[Channel];
    readonly errors: NotificationErrorMap;
  };
};

type RawSingleGrfqNotification =
  RawSingleGrfqNotificationByChannel[SingleGrfqWSChannel];

export type GrfqNotification =
  GrfqSingleNotificationByChannel[SingleGrfqWSChannel];

type DataProcessor = (
  data: RawNotificationDataByChannel[SingleGrfqWSChannel],
) => NotificationDataByChannel[SingleGrfqWSChannel];

type DataProcessorsByChannel = {
  readonly [Channel in SingleGrfqWSChannel]: (
    raw: RawNotificationDataByChannel[Channel],
  ) => NotificationDataByChannel[Channel];
};

const dataProcessors: DataProcessorsByChannel = {
  ticker(rawWsGrfqBbo) {
    return processWsGrfqBbo(rawWsGrfqBbo);
  },
};

interface RawSingleGrfqNotificationResp extends WSNotification {
  readonly params: RawSingleGrfqNotification;
}

function processNotification(
  raw: RawSingleGrfqNotificationResp,
): GrfqNotification {
  const { channel, data } = raw.params;
  const processor = dataProcessors[channel] as DataProcessor;
  return {
    channel,
    data: processor(data),
  } as GrfqNotification;
}
